import * as React from 'react';

import * as styles from './RecommendationDesktop.css';
import { Carousel } from '../Carousel';
import { UIHeading2 } from '@cian/ui-kit';
import * as classNames from 'classnames';
import { TRecommendation } from '../../types/recommendation';
import { useInView } from '@cian/valuation-utils-component';
import { Underground } from '../GeoInformation/components';
import { PriceDiscount } from '../PriceDiscount';

interface IRecommendationProps extends TRecommendation {
  onClick(analyticsData: object): void;
  onShowTrap(analyticsData: object): void;
  regionId: number;
  favoriteButton: React.ReactNode;
  labels: React.ReactNode;
  onTrack(offerId: number | null, position: number, contextToken?: string): void;
  position: number;
}

const CALLBACK_DELAY = 1000;

export const RecommendationDesktop: React.FC<IRecommendationProps> = ({
  onClick,
  onShowTrap,
  offerId,
  analyticsData,
  href,
  imageUrl,
  title: price,
  description,
  transport: address,
  underground,
  regionId,
  favoriteButton,
  labels,
  onTrack,
  position,
  discount,
  contextToken,
}) => {
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false,
  });

  const [trapShown, setTrapShown] = React.useState(false);
  const timerRef = React.useRef<number>();
  const [isHovered, setHovered] = React.useState(false);

  const mouseOver = React.useCallback(() => setHovered(true), []);
  const mouseOut = React.useCallback(() => setHovered(false), []);

  React.useEffect(() => {
    if (!offerId && inView && !trapShown) {
      onShowTrap(analyticsData);
      setTrapShown(true);
    }

    // копируется логика ActionAfterViewObserver, так как он не умеет не оборачивать компонент в DIV
    if (inView) {
      timerRef.current = window.setTimeout(() => {
        onTrack(offerId, position, contextToken);

        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }, CALLBACK_DELAY);

      return () => {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
      };
    } else {
      return undefined;
    }
  }, [inView, offerId, analyticsData, onShowTrap, trapShown, onTrack, position, contextToken]);

  const photos = React.useMemo(() => [{ id: 1, url: imageUrl }], [imageUrl]);

  return (
    <a
      ref={ref}
      onClick={() => onClick(analyticsData)}
      onMouseOver={mouseOver}
      onMouseLeave={mouseOut}
      className={styles['container']}
      href={href}
      target="_blank"
      rel="noreferrer"
      data-name="RecommendationDesktop"
    >
      {labels}
      <span className={styles['favorite']}>{favoriteButton}</span>
      <div className={styles['gallery']}>
        <Carousel media={photos} desktopControls={isHovered} />
      </div>

      <div className={styles['info']}>
        <div className={styles['price']}>
          <UIHeading2>{discount ? discount.newPrice : price}</UIHeading2>
          {discount && <PriceDiscount oldPrice={discount.oldPrice} percent={discount.percent} />}
        </div>
        <a href={href} target="_blank" rel="noreferrer" className={styles['description']}>
          {description}
        </a>
        {underground && <Underground underground={underground} regionId={regionId} />}
        <span className={classNames(styles['address'], !underground && styles['two-rows'])}>{address}</span>
      </div>
    </a>
  );
};
